import { mapGetters } from "vuex";
import { addDays } from "date-fns";
import {
  getProductImages,
  removeHTMLTagFromString,
  getImageByPosition,
} from "~/utils";

export default {
  data() {
    return {};
  },
  computed: {
    ...mapGetters({
      settings: "general/getSettings",
      breadcrumbs: "general/getBreadcrumbs",
      jsonSchemaData: "general/getJsonSchemaData",
      returnTimeframes: "general/getReturnTimeframes",
    }),
    cleanFrontDomain() {
      const frontDomain = this.settings?.front_domain;
      return frontDomain && frontDomain?.trim()?.endsWith("/")
        ? frontDomain.slice(0, -1)
        : frontDomain;
    },
    baseUrl() {
      return this.cleanFrontDomain || process.env.DOMAIN || "";
    },
    url() {
      return `${this.baseUrl}${this.$route.path}`;
    },
  },
  methods: {
    getBaseSchemaPayload() {
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: this.jsonSchemaData.pageName || this.settings?.company_name, // must be page title by default
        url: this.url,
        image:
          this.jsonSchemaData.pageImage || this.settings?.desktop_logo || "",
      };
    },
    getSchemaBreadcrumbs() {
      if (!this.breadcrumbs?.length) {
        return null;
      }
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: this.breadcrumbs?.map((el, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          item: {
            "@type": "WebPage",
            "@id": `${this.baseUrl}${this.localePath(el.href)}`,
            name: el.text,
            url: `${this.baseUrl}${this.localePath(el.href)}`,
          },
        })),
      };
    },
    setPageJsonLdSchema() {
      return {
        ...this.getBaseSchemaPayload(),
        breadcrumb: this.getSchemaBreadcrumbs(),
      };
    },
    setHomePageJsonLdSchema() {
      const payload = {
        ...this.getBaseSchemaPayload(),
        provider: {
          "@context": "https://schema.org",
          "@type": "JewelryStore",
          email: this.settings?.company_address?.email_address,
          telephone: this.settings?.company_address?.phone_number,
          openingHours: this.settings?.working_hours
            ? [this.settings.working_hours]
            : [],
          name: this.settings?.company_name,
          image: this.settings?.desktop_logo,
        },
      };
      if (this.settings?.company_address) {
        payload.provider.address = {
          "@type": "PostalAddress",
          streetAddress:
            (this.settings?.company_address?.address_line_1 || "") +
            (this.settings?.company_address?.address_line_2
              ? ", " + this.settings?.company_address?.address_line_2
              : ""),
          addressLocality: this.settings?.company_address?.city,
          addressRegion: this.settings?.company_address?.state,
          addressCountry: this.settings?.company_address?.country,
          postalCode: this.settings?.company_address?.zip,
        };
      }
      return payload;
    },
    setPLPPageLdSchema() {
      return {
        ...this.getBaseSchemaPayload(),
        breadcrumb: this.getSchemaBreadcrumbs(),
        mainEntity: {
          "@context": "https://schema.org",
          "@type": "ItemList",
          url: `${this.baseUrl}${this.$route.fullPath}`,
          numberOfItems: this.jsonSchemaData.numberOfItems,
          itemListElement: this.jsonSchemaData.products?.map((product) =>
            this.getBaseProductSchemaPayload(product)
          ),
        },
      };
    },
    /**
     * Constructs the base product schema payload used for structured data.
     *
     * @param {Object} product - The product object containing various details.
     * @param {Object} [options] - Additional options that can influence schema details.
     * @param {boolean} [options.out_of_stock] - Indicates whether the product is out of stock.
     * @return {Object} - The structured data product schema payload.
     */
    getBaseProductSchemaPayload(product, options) {
      const productTitle =
        this.jsonSchemaData?.productTitle ||
        product?.selected_config?.mixedTranslation?.title ||
        product?.variant_plp_image?.mixedTranslation?.title ||
        product?.default_config?.mixedTranslation?.title ||
        product?.mixedTranslation?.title ||
        "";

      const productDescription =
        this.jsonSchemaData?.productDescription ||
        product?.selected_config?.mixedTranslation?.description ||
        product?.variant_plp_image?.mixedTranslation?.description ||
        product?.default_config?.mixedTranslation?.description ||
        product?.mixedTranslation?.long_description ||
        "";

      const productPrice = Number(
        this.jsonSchemaData?.productPrice ||
          product?.selected_config?.cost ||
          product?.variant_plp_image?.cost ||
          product?.default_config?.cost ||
          product?.base_cost ||
          0
      );

      const discountValue = (() => {
        if (!product?.sale || !product?.sale_value || !product) {
          return 0;
        }

        return Number(
          (productPrice - productPrice * (+product.sale_value / 100)).toFixed(2)
        );
      })();

      const finalPrice = discountValue || productPrice || 0;

      const productImageUrl =
        this.jsonSchemaData?.productImage ||
        getProductImages(product)?.[0]?.url ||
        "/images/no-image.webp";

      const productSKU =
        this.jsonSchemaData?.productSku ||
        product?.selected_config?.configuration_id ||
        product?.variant_plp_image?.sku ||
        product?.default_config?.configuration_id ||
        product?.default_sku;

      let applicableCountry = this.$config?.LOCALE
        ? this.$config.LOCALE.toUpperCase()
        : "";
      applicableCountry = applicableCountry === "CS" ? "CZ" : applicableCountry;

      const getProductMerchantReturnDays = (product) => {
        let result = this.settings?.default_return_timeframe;
        if (this.returnTimeframes?.length) {
          const collectionIds =
            (product?.collections || [])?.map((collection) => collection.id) ||
            product?.collections_ids ||
            [];
          const categoryIds =
            (product?.categories || [])?.map((category) => category.id) ||
            product.categories_ids ||
            [];
          const timeframeDays = this.returnTimeframes.reduce(
            (acc, timeframe) => {
              if (
                (timeframe.collection_id &&
                  collectionIds.includes(timeframe.collection_id)) ||
                (timeframe.category_id &&
                  categoryIds.includes(timeframe.category_id))
              ) {
                acc =
                  acc === null || timeframe.days < acc ? timeframe.days : acc;
              }
              return acc;
            },
            null
          );
          result = timeframeDays !== null ? timeframeDays : result;
        }
        return result;
      };

      const schema = {
        "@type": "Product",
        image: productImageUrl,
        url:
          this.jsonSchemaData?.productUrl ||
          `${this.baseUrl}${this.localePath(`/product/${product?.slug}`)}`,
        name: productTitle,
        description: removeHTMLTagFromString(productDescription),
        sku: productSKU,
        brand: {
          "@type": "Brand",
          name: this.settings?.company_name,
        },
        offers: {
          "@type": "Offer",
          priceCurrency: this.settings?.currency_code || "",
          price: finalPrice.toFixed(2),
          priceValidUntil: addDays(new Date(), 1).toISOString().split("T")[0],
          availability:
            options?.out_of_stock || !finalPrice
              ? "https://schema.org/OutOfStock"
              : "https://schema.org/InStock",
          url:
            this.jsonSchemaData?.productUrl ||
            `${this.baseUrl}${this.localePath(`/product/${product?.slug}`)}`,
          hasMerchantReturnPolicy: {
            "@type": "MerchantReturnPolicy",
            applicableCountry,
            returnPolicyCategory:
              "https://schema.org/MerchantReturnFiniteReturnWindow",
            merchantReturnDays: getProductMerchantReturnDays(product),
            returnMethod: "https://schema.org/ReturnByMail",
            returnFees: "https://schema.org/FreeReturn",
          },
        },
      };

      // Add additionalProperty if available
      if (options?.additionalProperties?.length) {
        schema.additionalProperty = options.additionalProperties;
      }

      // Add shippingDetails if available
      if (options?.shippingDetails) {
        schema.offers.shippingDetails = options.shippingDetails;
      }

      // Add aggregateRating if available
      if (options?.aggregateRating) {
        schema.aggregateRating = options.aggregateRating;
      }

      // Add reviews if available
      if (options?.reviews?.length) {
        schema.review = options.reviews;
      }

      return schema;
    },
    hashCode(s) {
      /**
       * Using the java hashCode function
       * https://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
       * https://www.thejavaprogrammer.com/what-is-hashcode-in-java/
       */
      let hash = 0;
      for (let i = 0; i < s.length; i += 1) {
        const char = s.charCodeAt(i);
        hash = (hash << 5) - hash + char; // eslint-disable-line no-bitwise
        hash &= hash; // eslint-disable-line no-bitwise
      }
      return hash;
    },
    getJsonLdScriptHid(payload) {
      const minifiedString = JSON.stringify(payload, null, "");
      return `nuxt-jsonld-${this.hashCode(minifiedString).toString(16)}`;
    },
    /**
     * Constructs and returns a structured data payload for a product page based on schema.org specifications.
     *
     * @param {Object} options - Configuration options for constructing the product page schema.
     * @param {boolean} [options.out_of_stock] - Indicates if the product is out of stock, affecting inventory status in the schema.
     * @return {Object} Returns an object containing the structured data for a product page, including base schema data, breadcrumbs, and the main entity details.
     */
    /**
     * Parses additional properties string format to schema.org PropertyValue array
     * @param {string} detailsString - Format: "property1|value1;property2|value2"
     * @returns {Array|null} Array of PropertyValue objects or null
     */
    parseAdditionalPropertiesToSchema(detailsString) {
      if (!detailsString) return null;

      const properties = detailsString
        .split(";")
        .map((item) => {
          const [property, value] = item.split("|").map((str) => str.trim());
          if (property && value) {
            return {
              "@type": "PropertyValue",
              name: property,
              value,
            };
          }
          return null;
        })
        .filter((item) => item !== null);

      return properties.length > 0 ? properties : null;
    },
    /**
     * Gets additional properties for schema.org Product additionalProperty field
     * @returns {Array|null} Array of PropertyValue objects or null
     */
    getAdditionalPropertiesForSchema() {
      const product = this.jsonSchemaData?.product;
      if (!product) return null;

      // Priority 1: Check if additionalProperties are stored in jsonSchemaData
      if (this.jsonSchemaData?.additionalProperties?.length) {
        return this.jsonSchemaData.additionalProperties;
      }

      // Priority 2: Product details_map (array format)
      if (product?.mixedTranslation?.content?.details_map?.length) {
        return product.mixedTranslation.content.details_map
          .filter((item) => item.property && item.value)
          .map((item) => ({
            "@type": "PropertyValue",
            name: item.property,
            value: item.value,
          }));
      }

      return null;
    },
    /**
     * Gets shipping details for schema.org Product offers.shippingDetails field
     * Uses fixed delivery time values
     * @param {Object} options - Optional configuration
     * @param {string} options.shippingRate - Shipping cost (default: "0" for free shipping)
     * @param {string} options.shippingCountry - Shipping destination country code (default: from settings or LOCALE)
     * @param {number} options.handlingDaysMin - Minimum handling days (default: 10)
     * @param {number} options.handlingDaysMax - Maximum handling days (default: 21)
     * @param {number} options.transitDaysMin - Minimum transit days (default: 1)
     * @param {number} options.transitDaysMax - Maximum transit days (default: 4)
     * @returns {Object|null} ShippingDetails object or null if insufficient data
     */
    getShippingDetails(options = {}) {
      // Get shipping destination country
      let shippingCountry =
        options?.shippingCountry ||
        this.settings?.company_address?.country ||
        (this.$config?.LOCALE ? this.$config.LOCALE.toUpperCase() : null);

      // Handle special cases (e.g., CS -> CZ)
      if (shippingCountry === "CS") {
        shippingCountry = "CZ";
      }

      // If no country available, return null (don't include shippingDetails)
      if (!shippingCountry) {
        return null;
      }

      // Get shipping rate (default to 0 for free shipping)
      const shippingRate = options?.shippingRate ?? "0";
      const currency = this.settings?.currency_code || "EUR";

      // Fixed delivery time values
      const handlingDaysMin = options?.handlingDaysMin ?? 10;
      const handlingDaysMax = options?.handlingDaysMax ?? 21;
      const transitDaysMin = options?.transitDaysMin ?? 1;
      const transitDaysMax = options?.transitDaysMax ?? 4;

      return {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: shippingRate,
          currency,
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: shippingCountry,
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: handlingDaysMin,
            maxValue: handlingDaysMax,
            unitCode: "d",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: transitDaysMin,
            maxValue: transitDaysMax,
            unitCode: "d",
          },
        },
      };
    },
    /**
     * Calculates aggregate rating from reviews array
     * @param {Array} reviews - Array of review objects
     * @returns {Object|null} AggregateRating object or null if no valid reviews
     */
    getAggregateRatingFromReviews(reviews) {
      if (!reviews?.length) {
        return null;
      }

      // Filter reviews with valid star ratings
      const validReviews = reviews.filter(
        (review) => review.stars && review.stars > 0 && review.stars <= 5
      );

      if (validReviews.length === 0) {
        return null;
      }

      // Calculate average rating
      const totalStars = validReviews.reduce(
        (sum, review) => sum + review.stars,
        0
      );
      const averageRating = (totalStars / validReviews.length).toFixed(1);

      return {
        "@type": "AggregateRating",
        ratingValue: averageRating,
        reviewCount: validReviews.length,
        bestRating: "5",
        worstRating: "1",
      };
    },
    /**
     * Transforms reviews array to schema.org Review format
     * @param {Array} reviews - Array of review objects
     * @returns {Array|null} Array of Review objects or null
     */
    getReviewsSchema(reviews) {
      if (!reviews?.length) {
        return null;
      }

      const validReviews = reviews
        .filter(
          (review) =>
            review.stars &&
            review.stars > 0 &&
            review.stars <= 5 &&
            review.status === 1
        )
        .map((review) => {
          const translation =
            review.mixedTranslation || review.translations?.[0] || {};

          return {
            "@type": "Review",
            author: {
              "@type": "Person",
              name: review.author || "Anonymous",
            },
            datePublished: review.published_at || review.created_at,
            reviewBody: removeHTMLTagFromString(
              translation.description || ""
            ),
            name: translation.title || translation.subtitle || "",
            reviewRating: {
              "@type": "Rating",
              ratingValue: review.stars.toString(),
              bestRating: "5",
              worstRating: "1",
            },
          };
        });

      // Return null if no valid reviews after filtering
      return validReviews.length > 0 ? validReviews : null;
    },
    setProductPageLdSchema(options = {}) {
      return {
        ...this.getBaseSchemaPayload(),
        breadcrumb: this.getSchemaBreadcrumbs(),
        mainEntity: {
          "@context": "https://schema.org",
          ...this.getBaseProductSchemaPayload(this.jsonSchemaData.product, {
            out_of_stock: options?.out_of_stock,
            additionalProperties: options?.additionalProperties,
            shippingDetails: options?.shippingDetails,
            aggregateRating: options?.aggregateRating,
            reviews: options?.reviews,
          }),
        },
      };
    },
    setBlogListPageLdSchema() {
      return {
        ...this.getBaseSchemaPayload(),
        breadcrumb: this.getSchemaBreadcrumbs(),
        mainEntity: {
          "@context": "https://schema.org",
          "@type": "Blog",
          image:
            this.jsonSchemaData?.pageImage || this.settings?.desktop_logo || "",
          name: this.jsonSchemaData?.pageName || this.settings?.company_name,
          url: `${this.baseUrl}${this.$route.path}`,
          "@id": `${this.baseUrl}${this.$route.path}`,
          mainEntityOfPage: `${this.baseUrl}${this.$route.path}`,
          blogPost: this.jsonSchemaData?.posts?.map((post) => ({
            ...this.getBaseBlogSchemaPayload(post, { imagePosition: "list" }),
          })),
        },
      };
    },
    getBaseBlogSchemaPayload(post, options) {
      const postUrl =
        options?.id ||
        `${this.baseUrl}${this.localePath(
          `/articles/${post?.categories?.[0]?.slug || "all"}/${post.slug}`
        )}`;
      return {
        "@type": "BlogPosting",
        "@id": postUrl,
        url: postUrl,
        // mainEntityOfPage: `${baseUrl}/blog/${blog?.categories[0]?.slug || "all"}/${blog.slug}`,
        headline: post?.mixedTranslation?.title || "",
        name: post?.mixedTranslation?.title || "",
        description: removeHTMLTagFromString(
          post?.mixedTranslation?.meta_description ||
            post?.mixedTranslation?.short_description ||
            ""
        ),
        datePublished: post.created_at,
        dateModified: post.updated_at,
        // author: { // TODO define who is author
        //   "@type": "Person",
        //   "@id": "https://dataliberate.com/author/richard-wallis/#Person",
        //   name: "Richard Wallis",
        // },
        image:
          getImageByPosition(post.medias, options.imagePosition)?.file?.url ||
          "/images/no-image.webp",
      };
    },
    setBlogPostPageLdSchema() {
      return {
        ...this.getBaseSchemaPayload(),
        breadcrumb: this.getSchemaBreadcrumbs(),
        mainEntity: {
          ...this.getBaseBlogSchemaPayload(this.jsonSchemaData?.post, {
            id: this.url,
            imagePosition: "page",
          }),
          isPartOf: {
            "@type": "Blog",
            "@id": `${this.baseUrl}${this.localePath("/articles")}`,
            name: this.$t("articles"),
            publisher: {
              "@type": "Organization",
              "@id": this.baseUrl + this.localePath("/"),
              name: this.settings?.company_name,
            },
          },
        },
      };
    },
    /**
     * Get locale information from i18n
     * @returns {Object} Locale configuration
     * @private
     */
    _getLocaleConfig() {
      const currentLocale = this.$i18n?.locale || this.$config?.LOCALE || "en";
      const defaultLocale =
        this.$i18n?.defaultLocale || this.$config?.LOCALE || "en";

      return {
        current: currentLocale,
        default: defaultLocale,
        isEnglishOnNonEnglishDomain:
          currentLocale === "en" && defaultLocale !== "en",
      };
    },
    /**
     * Map locale codes to file name formats
     * @param {string} locale - Locale code
     * @returns {string} Mapped locale for file name
     * @private
     */
    _mapLocaleToFileName(locale) {
      const localeMap = {
        pl: "pl",
        cs: "cz",
        sk: "sk",
        es: "es",
        de: "at",
        nl: "nl",
        en: "en",
      };
      return localeMap[locale] || locale;
    },
    /**
     * Load JSON-LD data from static files
     * @param {Object} config - Configuration object
     * @param {string} config.directory - Directory name (e.g., 'faqjsonld')
     * @param {string} config.fileName - File name to load
     * @param {string} config.fallbackFileName - Fallback file name
     * @param {string} config.type - Type name for logging
     * @returns {Object|null} JSON-LD data
     * @private
     */
    _loadJsonLdData(config) {
      const { directory, fileName, fallbackFileName, type } = config;

      try {
        return require(`@/static/${directory}/${fileName}`);
      } catch (error) {
        console.warn(`${type} JSON-LD not found: ${fileName}`);

        // Try fallback if provided
        if (fallbackFileName) {
          try {
            return require(`@/static/${directory}/${fallbackFileName}`);
          } catch (fallbackError) {
            console.error(
              `${type} JSON-LD fallback failed: ${fallbackFileName}`
            );
          }
        }

        return null;
      }
    },
    /**
     * Get FAQ JSON-LD for the current locale
     * Uses i18n to determine current locale
     * @returns {Object|null} FAQ JSON-LD data
     */
    getFaqJsonLdData() {
      const localeConfig = this._getLocaleConfig();
      const mapped = this._mapLocaleToFileName(localeConfig.current);

      return this._loadJsonLdData({
        directory: "faqjsonld",
        fileName: `${mapped}.json`,
        fallbackFileName: "en.json",
        type: "FAQ",
      });
    },
    /**
     * Check if current page matches any of the given identifiers
     * @param {Array<string>} identifiers - Array of URL identifiers
     * @returns {boolean}
     * @private
     */
    _isPageType(identifiers) {
      const path = this.$route?.path?.toLowerCase() || "";
      const slug = this.$route?.params?.pathMatch?.toLowerCase() || "";

      return identifiers.some(
        (identifier) => path.includes(identifier) || slug.includes(identifier)
      );
    },
    /**
     * Check if the current page is an FAQ page based on slug or URL
     * @returns {boolean}
     */
    isFaqPage() {
      const faqIdentifiers = [
        "faq",
        "faqs",
        "najczestejsze-pytania", // Polish
        "najcastejsie-otazky", // Slovak
        "frequently-asked-questions", // English
        "preguntas-frecuentes", // Spanish
        "haufig-gestellte-fragen", // German
        "nejcasteji-otazky", // Czech
      ];

      return this._isPageType(faqIdentifiers);
    },
    /**
     * Wrap JSON-LD data in WebPage schema with breadcrumbs
     * @param {Object} data - JSON-LD data to wrap
     * @param {boolean} includeWrapper - If true, wraps in WebPage schema
     * @returns {Object} Wrapped or unwrapped JSON-LD data
     * @private
     */
    _wrapJsonLdWithPage(data, includeWrapper = true) {
      if (!includeWrapper) {
        return data;
      }

      return {
        ...this.getBaseSchemaPayload(),
        breadcrumb: this.getSchemaBreadcrumbs(),
        mainEntity: data,
      };
    },
    /**
     * Generic method to set page-specific JSON-LD schema
     * @param {Function} isPageTypeFn - Function to check if it's the correct page type
     * @param {Function} getDataFn - Function to get the JSON-LD data
     * @param {boolean} includePageWrapper - If true, wraps in WebPage schema
     * @returns {Object|null} JSON-LD payload or null
     * @private
     */
    _setPageJsonLdSchema(isPageTypeFn, getDataFn, includePageWrapper = true) {
      if (!isPageTypeFn.call(this)) {
        return null;
      }

      const data = getDataFn.call(this);

      if (!data) {
        return null;
      }

      return this._wrapJsonLdWithPage(data, includePageWrapper);
    },
    /**
     * Set FAQ page JSON-LD schema
     * Returns complete FAQ page schema with breadcrumbs if on FAQ page, null otherwise
     * @param {boolean} includePageWrapper - If true, wraps FAQPage in WebPage schema with breadcrumbs
     * @returns {Object|null} FAQ JSON-LD payload or null
     */
    setFaqPageJsonLdSchema(includePageWrapper = true) {
      return this._setPageJsonLdSchema(
        this.isFaqPage,
        this.getFaqJsonLdData,
        includePageWrapper
      );
    },
    /**
     * Get About Us JSON-LD for the current locale and language
     * Uses i18n to determine current and default locale
     * @returns {Object|null} About Us JSON-LD data
     */
    getAboutUsJsonLdData() {
      const localeConfig = this._getLocaleConfig();
      const mappedCurrent = this._mapLocaleToFileName(localeConfig.current);
      const mappedDefault = this._mapLocaleToFileName(localeConfig.default);

      let fileName;
      let fallbackFileName = null;

      // Check if user is viewing English version on non-English domain
      if (localeConfig.isEnglishOnNonEnglishDomain) {
        // English version on non-English domain (e.g., /en/unternehmen on omara.at)
        fileName = `about_us_EN_${mappedDefault}.json`;
        fallbackFileName = `about_us_${mappedDefault}.json`;
      } else {
        // Regular locale version
        fileName = `about_us_${mappedCurrent}.json`;
      }

      return this._loadJsonLdData({
        directory: "aboutusjsonld",
        fileName,
        fallbackFileName,
        type: "About Us",
      });
    },
    /**
     * Check if the current page is an About Us page based on slug or URL
     * @returns {boolean}
     */
    isAboutUsPage() {
      const aboutUsIdentifiers = [
        "about-us",
        "about",
        "o-nas",
        "firma",
        "informacje-prawne", // Polish
        "spolecnost", // Czech
        "spolocnost", // Slovak
        "empresa", // Spanish
        "unternehmen", // German/Austrian
        "bedrijf", // Dutch
        "company",
        "legal-information",
      ];

      return this._isPageType(aboutUsIdentifiers);
    },
    /**
     * Set About Us page JSON-LD schema
     * Returns JewelryStore schema if on About Us page, null otherwise
     * @param {boolean} includePageWrapper - If true, wraps in WebPage schema with breadcrumbs
     * @returns {Object|null} About Us JSON-LD payload or null
     */
    setAboutUsPageJsonLdSchema(includePageWrapper = true) {
      return this._setPageJsonLdSchema(
        this.isAboutUsPage,
        this.getAboutUsJsonLdData,
        includePageWrapper
      );
    },
  },
};
